"use client";

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import gsap from "gsap";
import { fetchTrades, type Trade } from "@/lib/api";
import { tokenHsla } from "@/lib/theme";

const fmtPrice = (v: string) =>
  Number(v).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (v: string) =>
  Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 });
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" });

/**
 * A row animates on mount only if its trade happened after this tab opened.
 *
 * React keys already guarantee that only genuinely new trades mount new Rows, so "did I just
 * mount?" is most of the signal: the timestamp check is what stops the initial backfill
 * (~21h of history) from flashing all at once on first paint.
 */
const Row = ({ trade, watchingSince }: { trade: Trade; watchingSince: number }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (new Date(trade.traded_at).getTime() <= watchingSince) return;
    const el = ref.current;
    if (!el) return;

    // This market fills ~5x/hour, so a print is a real event worth drawing the eye to, 
    // unlike a busy venue where constant motion is just noise.
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.from(el, { opacity: 0, x: -8, duration: 0.35, ease: "power2.out" });
      gsap.fromTo(
        el,
        // tokenHsla, NOT `hsl(var(--…))`: GSAP cannot parse var() inside a color and crashes
        // on every ticker frame if handed one (see tokenHsla docs).
        { backgroundColor: tokenHsla(`--${trade.side}`, 0.22, "transparent") },
        { backgroundColor: "transparent", duration: 1.1, ease: "power2.out" }
      );
    });
    return () => mm.revert();
    // Mount-only by design: a row's identity is its trade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={ref} className="flex justify-between px-2 py-0.5 text-xs">
      <span className={`tnum ${trade.side === "buy" ? "text-buy" : "text-sell"}`}>
        {fmtPrice(trade.price)}
      </span>
      <span className="tnum text-fg-secondary">{fmtQty(trade.quantity)}</span>
      <span className="tnum text-fg-muted">{fmtTime(trade.traded_at)}</span>
    </div>
  );
};

const TradesTape = ({ symbol }: { symbol: string }) => {
  // Lazy initialiser: evaluated once, so it's a stable "when did this tab start watching?".
  const [watchingSince] = useState(() => Date.now());

  const { data, isLoading, isError } = useQuery({
    queryKey: ["trades", symbol],
    queryFn: ({ signal }) => fetchTrades(symbol, 40, signal),
    refetchInterval: 10_000,
  });

  if (isLoading) return <div className="p-3 text-xs text-fg-muted">Loading trades…</div>;
  if (isError) return <div className="p-3 text-xs text-sell">Trades unavailable</div>;

  const trades = data?.trades ?? [];
  if (trades.length === 0)
    return <div className="p-3 text-xs text-fg-muted">No trades yet.</div>;

  return (
    <div className="flex h-full flex-col">
      <div className="flex justify-between border-b border-line px-2 py-1.5 text-[11px] uppercase tracking-wide text-fg-muted">
        <span>Price (NGN)</span>
        <span>Size (USD)</span>
        <span>Time</span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {trades.map((t) => (
          <Row key={t.id} trade={t} watchingSince={watchingSince} />
        ))}
      </div>
    </div>
  );
};

export default TradesTape;
